// LA SECCIÓN «PESO» DE SEGUIMIENTO EN LA FICHA DEL PANEL (Francisco, 21-10).
//
//     Peso actual: 77,1 kg · Peso últimos macros: 78,4 kg · Peso inicial: 94 kg
//     Ha perdido 16,9 kg de peso desde el primer peso registrado
//
// EL ACTUAL ES EL DE LA CABECERA DE LA FICHA (`pesoActual`): el último punto de la serie
// `client_profiles.pesos` hasta hoy, que es lo que el servidor llama peso actual
// (`core/series_cliente.actual`), y el campo suelto de la ficha si no hay serie. Una sola
// función para los dos sitios: dos «Peso actual» distintos en la misma ficha es el punto 9.
//
// El inicial sale de LA CURVA que pinta «Evolución del peso» justo debajo y que ve el
// cliente en su Evolución (`GET /admin/clients/{id}/evolucion`): la serie más los pesos que
// viajaron con un ajuste y los de los reportes, saneada, hasta hoy y con su marca `fiable`.
// Ahí está su historia de antes de la serie (Calma), que es el «primer peso registrado».
// Los saltos imposibles no cuentan (`core/series_cliente.marcar_los_saltos_imposibles`): con
// un 118 de la importación como primer peso, la frase diría que ha perdido cuarenta kilos.
//
// El de los últimos macros es el de la fila que rige del historial, con la fecha de su
// pesaje (`lib/historialDeMacros.pesoDeLosUltimosMacros`).
//
// Lo que no hay, no se inventa: null y la pantalla dice «sin dato».

import { num1 } from './numeros';
import { pesoDeLosUltimosMacros } from './historialDeMacros';
import { hoyEnEspana, hoyLocal } from './horaEspana';

// El último punto de una serie {fecha, valor} (punto 30), HASTA HOY (punto 22): en la ficha
// de `hola@jesusgallegopt.com`, con 29 puntos de 2027 y 2028 de la importación, el Resumen
// enseñaba «118 kg · del 21/02/2028» cuando el último pesaje de verdad eran 77,1 kg de hoy.
// Vale para el peso y para el % graso. Vivía dentro de ClientDetailPage hasta el 21-10.
export const ultimoDeLaSerie = (serie, hoy = hoyEnEspana()) => {
    const puntos = (serie || []).filter(x => x?.valor != null && x?.fecha && String(x.fecha).slice(0, 10) <= hoy);
    if (!puntos.length) return null;
    return puntos.reduce((a, b) => String(b.fecha) > String(a.fecha) ? b : a);
};

/** El peso actual de una ficha, `{ peso, fecha }`: el último de su serie y, si es una ficha
 *  vieja sin serie, el campo suelto (`weight`, sin fecha). null si no hay ninguno. */
export const pesoActual = (perfil, hoy = hoyEnEspana()) => {
    const p = ultimoDeLaSerie(perfil?.pesos, hoy);
    if (p) return { peso: p.valor, fecha: String(p.fecha).slice(0, 10) };
    return perfil?.weight ? { peso: perfil.weight, fecha: null } : null;
};

/** El peso que apuntó HOY, o null si hoy no se ha pesado. El día es el suyo, el del reloj
 *  de su navegador (`hoyLocal`): es el que apunta al pesarse. Lo pregunta la tarjeta del
 *  quincenal (24-10): con el peso de hoy ya puesto no se le pide otra vez, se le dice. */
export const pesoDeHoy = (perfil, hoy = hoyLocal()) => {
    const p = pesoActual(perfil, hoy);
    return p?.fecha === hoy ? p.peso : null;
};

const _fiables = (curva) => (curva || [])
    .map(p => ({ fecha: String(p?.date ?? p?.fecha ?? '').slice(0, 10), peso: p?.value ?? p?.peso, fiable: p?.fiable }))
    .filter(p => p.fecha && typeof p.peso === 'number' && p.peso > 0 && p.fiable !== false)
    .sort((a, b) => a.fecha.localeCompare(b.fecha));

/** La frase del cambio, o null si solo hay un peso. */
export const fraseDelCambio = (cambio) => {
    if (cambio == null) return null;
    if (cambio < 0) return `Ha perdido ${num1(-cambio)} kg de peso desde el primer peso registrado`;
    if (cambio > 0) return `Ha ganado ${num1(cambio)} kg de peso desde el primer peso registrado`;
    return 'Pesa lo mismo que el primer peso registrado';
};

/**
 * `curva`: la `weight` de `/evolucion` ([{ date, value, fiable }]). `historial`: el
 * `macro_history` de la ficha, de la más reciente a la más antigua. `perfil`: la ficha.
 * Devuelve { actual, ultimosMacros, inicial, cambio, frase }; cada peso es { peso, fecha } o null.
 *
 * Sin frase si solo hay un peso: el inicial es el mismo pesaje que el actual.
 */
export function resumenDelPeso(curva, historial, perfil, hoy = hoyEnEspana()) {
    const puntos = _fiables(curva);
    const actual = pesoActual(perfil, hoy);
    const primero = puntos.length ? puntos[0] : null;
    const inicial = primero ? { peso: primero.peso, fecha: primero.fecha } : null;
    const mismoPesaje = !actual || !inicial || puntos.length < 2
        || (actual.fecha != null && actual.fecha <= inicial.fecha);
    const cambio = mismoPesaje ? null : Math.round((actual.peso - inicial.peso) * 10) / 10;
    return {
        actual,
        ultimosMacros: pesoDeLosUltimosMacros(historial),
        inicial,
        cambio,
        frase: fraseDelCambio(cambio),
    };
}
